import React, { useRef } from "react";

const PhotosGallery = ({
  currentTab,
  isMatching,
  canUpload,
  elapsedTime,
  formatTime,
  estimateTimeLeft,
  displayedPhotos,
  columnCount,
  getInterleavedColumns,
  selectedPhotos,
  handleImageClick,
  togglePhotoSelection,
  getThumbUrl,
  getBrowserSafeUrl,
  handleUpload,
  selectionMode,
  setSelectionMode,
}) => {
  const longPressTimer = useRef(null);
  const touchStartPos = useRef(null);
  const isLongPressTriggered = useRef(false);

  const handleTouchStart = (e, photoId) => {
    isLongPressTriggered.current = false;
    if (e.touches && e.touches.length > 0) {
      touchStartPos.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
      longPressTimer.current = setTimeout(() => {
        isLongPressTriggered.current = true;
        setSelectionMode(true);
        if (!selectedPhotos.has(photoId)) {
          togglePhotoSelection(photoId);
        }
      }, 500);
    }
  };

  const handleTouchMove = (e) => {
    if (!touchStartPos.current || !e.touches || e.touches.length === 0) return;
    const moveX = e.touches[0].clientX;
    const moveY = e.touches[0].clientY;
    const diffX = Math.abs(moveX - touchStartPos.current.x);
    const diffY = Math.abs(moveY - touchStartPos.current.y);
    
    if (diffX > 10 || diffY > 10) {
      clearTimeout(longPressTimer.current);
      touchStartPos.current = null;
    }
  };

  const handleTouchEnd = () => {
    clearTimeout(longPressTimer.current);
    touchStartPos.current = null;
  };

  const onImageClick = (e, globalIndex) => {
    if (isLongPressTriggered.current) {
      isLongPressTriggered.current = false;
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    handleImageClick(globalIndex);
  };
  // Face matching loader
  if (currentTab === "my" && isMatching) {
    return (
      <div className="face-match-loader">
        <div className="spinner"></div>
        <h3>🔍 Finding Your Photos</h3>
        <p>⏱ Time elapsed: {formatTime(elapsedTime)}</p>
        <p>⏳ Estimated time: {estimateTimeLeft()}</p>
        <small>Using AI to detect your face in photos...</small>
      </div>
    );
  }

  // Photos masonry grid
  if (displayedPhotos.length > 0) {
    return (
      <div className={`photos-masonry-wrapper ${selectionMode ? "selection-active" : ""}`}>
        {getInterleavedColumns(displayedPhotos, columnCount).map((colPhotos, colIdx) => (
          <div key={colIdx} className="masonry-column">
            {colPhotos.map(({ photo, globalIndex }) => {
              const photoId = photo._id || photo.filePath;
              const isSelected = selectedPhotos.has(photoId);

              return (
                <div
                  key={photo._id || globalIndex}
                  className={`photo-card ${isSelected ? "selected" : ""}`}
                  onClick={(e) => onImageClick(e, globalIndex)}
                  onTouchStart={(e) => handleTouchStart(e, photoId)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  onTouchCancel={handleTouchEnd}
                >
                  <div
                    className={`photo-corner-checkbox ${isSelected ? "checked" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePhotoSelection(photoId);
                    }}
                    title={isSelected ? "Deselect Photo" : "Select Photo"}
                  >
                    {isSelected && "✓"}
                  </div>

                  <img
                    src={getThumbUrl(photo)}
                    onError={(e) => {
                      const target = e.target;
                      // Fallback chain: Sharp WebP → original filePath
                      if (!target.dataset.triedOriginal) {
                        target.dataset.triedOriginal = "true";
                        target.src = getBrowserSafeUrl(photo.filePath);
                        return;
                      }
                      target.onerror = null;
                    }}
                    alt={photo.fileName || `Photo ${globalIndex + 1}`}
                    className="photo-img"
                    loading="lazy"
                  />
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  }

  // Empty state
  if (!canUpload) {
    return (
      <div className="empty-state-readonly">
        <div className="empty-icon">📁</div>
        <h3>No Photos Found</h3>
      </div>
    );
  }

  // Empty state dropzone for admins
  return (
    <label
      className="empty-state-dropzone"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
          handleUpload({ target: { files: e.dataTransfer.files } });
        }
      }}
    >
      <input
        type="file"
        multiple
        className="empty-dropzone-input"
        onChange={handleUpload}
      />
      <div className="empty-icon">📁</div>
      <h3>No Photos in this Album Yet</h3>
      <p>Drag & drop your photos anywhere here or click to browse files</p>
      <span className="dropzone-upload-btn">
        <span>📤</span> Upload Photos
      </span>
    </label>
  );
};

export default PhotosGallery;
